import { lazy, Suspense } from "react";
import { Global } from "@emotion/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { BrowserRouter, Redirect, Route, Switch } from "react-router-dom";
import { Auth } from "@newLayouts/auth/Auth";
import { AuthRoute } from "@newLayouts/common/AuthRoute";
import { ErrorLayout } from "@newLayouts/common/ErrorLayout";
import { ViewportCenterCircle } from "@newComponents/common/ViewportCenterCircle";
import { AppStageBadge } from "@newComponents/layout/AppStageBadge";
import { cleanupUnusedStorage, setDefaultHttpTimezoneOffsetHeader } from "@newLibs/utils";
import { MyInfoProvider } from "@newHooks/context/myInfo/myInfo.context";
import { OrganizationProvider } from "@newHooks/context/organization/organization.context";
import { SessionContextProvider } from "@newHooks/context/session/session.context";
import { NotificationContextProvider } from "@newHooks/context/snackbar/notification.context";
import { globalStyles } from "./assets/styles/globalStyles";

const NewHomeLayout = lazy(() => import("@newRoutes/home/Index"));
const NewSignerLayout = lazy(() => import("@newRoutes/signer/Index"));
const NewSharingLayout = lazy(() => import("@newRoutes/sharing/Index"));
const NewReferrerLayout = lazy(() => import("@newRoutes/referrer/Index"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      cacheTime: 0,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      retry: () => false,
    },
  },
});

function App() {
  cleanupUnusedStorage();
  setDefaultHttpTimezoneOffsetHeader();

  return (
    <NotificationContextProvider>
      <SessionContextProvider>
        <QueryClientProvider client={queryClient}>
          <AppStageBadge />

          <BrowserRouter>
            <Global styles={globalStyles} />
            <Suspense fallback={<ViewportCenterCircle />}>
              <Switch>
                <Route exact path="/404" render={ErrorLayout} />
                <Route exact path="/auth" render={() => <Auth />} />
                <Route path="/signers" component={NewSignerLayout} />
                <Route path="/sharings" component={NewSharingLayout} />
                <Route path="/referrers" component={NewReferrerLayout} />
                <AuthRoute
                  authStrategy="AUTHORIZED_ONLY"
                  path="/"
                  render={() => (
                    <MyInfoProvider>
                      <OrganizationProvider>
                        <NewHomeLayout />
                      </OrganizationProvider>
                    </MyInfoProvider>
                  )}
                />
                <Redirect from="*" to="/404?r=rt" />
              </Switch>
            </Suspense>
          </BrowserRouter>
          {process.env.REACT_APP_STAGE === "local" && <ReactQueryDevtools />}
        </QueryClientProvider>
      </SessionContextProvider>
    </NotificationContextProvider>
  );
}

export default App;
